import { MarkerType, Position } from 'reactflow';
import type { FieldMapping, TransformationStep } from '../../services/api';
import type { CanvasEdge } from '../types';
import { clamp, edgePaletteColor, edgeStrokeColor, edgeStrokeDash, stableHash } from './edgeStyling';

export const buildEdgeId = (sourceId: string, targetId: string, suffix: string): string => `${sourceId}::${targetId}::${suffix}`;

export const createEdge = (
  edges: CanvasEdge[],
  sourceId: string,
  targetId: string,
  label: string | undefined,
  mapping: FieldMapping,
  step?: TransformationStep,
  confidence?: number,
  aiGenerated?: boolean,
  color?: string,
  edgeOptions?: { offset?: number; borderRadius?: number; jitterScale?: number }
) => {
  const id = buildEdgeId(sourceId, targetId, step?.step_id ?? mapping.mapping_id);
  if (edges.some((edge) => edge.id === id)) {
    return;
  }
  const defaultOffset = (() => {
    const hash = stableHash(id);
    const normalised = (hash % 200) / 199 - 0.5; // range [-0.5, 0.5]
    return clamp(normalised * 220, -220, 220);
  })();
  const baseOffset = edgeOptions?.offset ?? 0;
  const jitterScale = edgeOptions?.jitterScale ?? 0.35;
  const resolvedOffset = clamp(baseOffset + defaultOffset * jitterScale, -240, 240);
  const resolvedBorderRadius = clamp(edgeOptions?.borderRadius ?? 32, 8, 80);
  const strokeColor = edgeStrokeColor(mapping, step, confidence, color);
  edges.push({
    id,
    source: sourceId,
    target: targetId,
    targetHandle: Position.Left,
    sourceHandle: Position.Right,
    type: 'confidence',
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: strokeColor,
    },
    label,
    animated: Boolean(aiGenerated) && mapping.field_status !== 'inherited',
    style: {
      strokeWidth: mapping.field_status === 'broken' ? 2.5 : 2.0,
      stroke: strokeColor,
      ...edgeStrokeDash(mapping, confidence),
    },
    data: {
      label: (() => {
        const raw = step?.expression || step?.operation || label;
        if (!raw || typeof raw !== 'string' || raw === 'direct') return undefined; // excessive noise
        return raw.length > 20 ? raw.slice(0, 18) + '..' : raw;
      })(),
      mappingId: mapping.mapping_id,
      stepId: step?.step_id,
      aiGenerated,
      confidence: confidence ?? mapping.confidence,
      offset: resolvedOffset,
      borderRadius: resolvedBorderRadius,
      // Phase 2B: Transformation type and expression for edge icons
      transformationType: String(step?.operation || step?.kind || mapping.transformation_type || label || 'direct'),
      transformation: typeof (step?.expression || mapping.transformation) === 'string'
        ? (step?.expression || mapping.transformation)
        : undefined,
    },

  });
};
